/**
 * API Helper Utilities
 * Common functions for API request handling, error processing, and response formatting
 */
import { appConfig } from '../config/environment';

/**
 * Standardized API response format
 */
export const createApiResponse = (data, status = 'success', message = null, metadata = {}) => ({
  data,
  status,
  message,
  metadata: {
    timestamp: new Date().toISOString(),
    ...metadata
  }
});

/**
 * Standardized error response format
 */
export const createApiError = (error, context = 'Unknown') => {
  console.error(`[${context}] API Error:`, error);

  let errorType = 'UNKNOWN_ERROR';
  let message = 'An unexpected error occurred';

  if (error.response) {
    const status = error.response.status;
    
    switch (status) {
      case 401:
        errorType = 'AUTH_ERROR';
        message = 'Authentication required or token expired';
        break;
      case 403:
        errorType = 'PERMISSION_ERROR';
        message = 'Insufficient permissions';
        break;
      case 404:
        errorType = 'NOT_FOUND_ERROR';
        message = 'Requested resource not found';
        break;
      case 429:
        errorType = 'RATE_LIMIT_ERROR';
        message = 'Too many requests. Please try again later';
        break;
      case 500:
      case 502:
      case 503:
      case 504:
        errorType = 'SERVER_ERROR';
        message = 'Server temporarily unavailable';
        break;
      default:
        errorType = 'HTTP_ERROR';
        message = error.response.data?.message || `HTTP ${status} error`;
    }
  } else if (error.request) {
    errorType = 'NETWORK_ERROR';
    message = 'Network error. Please check your connection';
  } else if (error.code === 'ECONNABORTED') {
    errorType = 'TIMEOUT_ERROR';
    message = 'Request timeout. Please try again';
  }

  return createApiResponse(null, 'error', message, {
    errorType,
    originalError: error.message,
    context
  });
};

/**
 * Add authentication header if token is available
 */
export const addAuthHeader = (config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers = {
      ...config.headers,
      'Authorization': `Bearer ${token}`
    };
  }
  return config;
};

/**
 * Handle successful API response
 */
export const handleApiResponse = (response) => {
  const data = response.data;
  
  // Handle different response formats
  if (Array.isArray(data)) {
    return createApiResponse(data, 'success', null, { count: data.length });
  }
  
  if (data && typeof data === 'object' && 'results' in data) {
    return createApiResponse(data.results, 'success', null, { 
      count: data.results.length,
      total: data.total || data.results.length
    });
  }
  
  if (data && typeof data === 'object') {
    return createApiResponse(data, 'success');
  }
  
  return createApiResponse(data, 'success');
};

/**
 * Create retry mechanism with exponential backoff
 */
export const createRetryFunction = (fn, context = 'API', maxRetries = appConfig.retry.attempts) => {
  return async (...args) => {
    let lastError;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await fn(...args);
      } catch (error) {
        lastError = error;
        
        // Don't retry on authentication or permission errors
        if (error.response?.status === 401 || error.response?.status === 403) {
          break;
        }
        
        // Don't retry on the last attempt
        if (attempt === maxRetries) {
          break;
        }
        
        const delay = appConfig.retry.backoff * Math.pow(2, attempt - 1);
        console.warn(`[${context}] Attempt ${attempt} failed, retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    throw lastError;
  };
};

/**
 * Check if error indicates service is online but has other issues
 */
export const isServiceOnline = (error) => {
  // If we get any HTTP response, the service is technically "online"
  return error.response && error.response.status;
};

/**
 * Extract rate limit information from response headers
 */
export const extractRateLimitInfo = (headers) => {
  if (!headers) return null;
  
  const remaining = headers['x-ratelimit-remaining'];
  const limit = headers['x-ratelimit-limit'];
  const reset = headers['x-ratelimit-reset'];
  
  if (remaining === undefined) return null;
  
  return {
    remaining: parseInt(remaining) || 0,
    limit: parseInt(limit) || 0,
    resetTime: reset ? new Date(parseInt(reset) * 1000) : null
  };
};

/**
 * Create timeout promise
 */
export const createTimeout = (ms, errorMessage = 'Request timeout') => {
  return new Promise((_, reject) => {
    setTimeout(() => reject(new Error(errorMessage)), ms);
  });
};